import { Badge, FilterChipGroup } from "@ndd/ui";
import { useState } from "react";
import {
  interpolateRd,
  promptHasSlot,
  rdFirstName,
  type StarterPromptGroup,
} from "../../../../shared/starter-prompts";

interface StarterPromptGroupsProps {
  groups: StarterPromptGroup[];
  rdName: string;
  /** Starter rows fill the composer so supervisors can edit before sending. */
  onSelectPrompt: (text: string) => void;
}

function chipLabel(group: StarterPromptGroup): string {
  return `${group.emoji} ${group.label}`;
}

export function StarterPromptGroups({
  groups,
  rdName,
  onSelectPrompt,
}: StarterPromptGroupsProps) {
  // `activeKey` is a preference, resolved at render: if the selected group
  // disappears (admin edit, library reload), fall back to the first group.
  const [activeKey, setActiveKey] = useState(groups[0]?.key ?? "");
  const activeGroup = groups.find((group) => group.key === activeKey) ?? groups[0];
  if (!activeGroup) return null;

  const firstName = rdFirstName(rdName);

  const filters = groups.map((group) => ({
    label: chipLabel(group),
    value: group.key,
    active: group.key === activeGroup.key,
  }));

  return (
    <div className="mt-6 w-full">
      <div className="flex justify-center">
        <FilterChipGroup
          filters={filters}
          onSelect={(filter) => {
            if (filter.value) setActiveKey(filter.value);
          }}
          showMoreFilters={false}
        />
      </div>
      <p className="mt-6 text-center text-body-sm text-subtle">
        {interpolateRd(activeGroup.description, firstName)}
      </p>
      <div className="mt-2 space-y-2">
        {activeGroup.prompts.map((prompt, promptIndex) => {
          const promptText = interpolateRd(prompt.text, firstName);

          return (
            <button
              key={promptIndex}
              type="button"
              onClick={() => onSelectPrompt(promptText)}
              className="flex min-h-[46px] w-full cursor-pointer items-center justify-between gap-3 rounded-xl border border-border bg-white px-4 py-3 text-left text-body-lg leading-[1.5] text-sand-600 shadow-sm transition-colors hover:bg-sand-50"
            >
              <span>{promptText}</span>
              {promptHasSlot(prompt.text) ? (
                <Badge variant="outline" className="shrink-0">
                  ✎ edit first
                </Badge>
              ) : (
                <span aria-hidden className="shrink-0 text-sm text-sand-300">
                  ↘
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
